"use client";

import { Button } from "@ethera/ui/button";
import { useTranslation } from "@guru/shared/i18n";
import { useUIStore } from "@guru/shared/stores";
import { Download, X } from "lucide-react";
import { useEffect, useState } from "react";
import { usePWAInstall } from "@/hooks/use-pwa-install";

const BANNER_DISMISSED_KEY = "pwa-install-banner-dismissed";

export function PWAInstallBanner() {
  const { t } = useTranslation();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(true); // Start hidden
  const setPwaBannerVisible = useUIStore((s) => s.setPwaBannerVisible);

  useEffect(() => {
    // Check if user previously dismissed
    const wasDismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
    setDismissed(!!wasDismissed);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, "true");
    setDismissed(true);
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      handleDismiss();
    }
  };

  const isVisible = !isInstalled && isInstallable && !dismissed;

  useEffect(() => {
    setPwaBannerVisible(isVisible);
    return () => setPwaBannerVisible(false);
  }, [isVisible, setPwaBannerVisible]);

  // Don't show if installed, not installable, or dismissed
  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-primary text-primary-foreground shadow-lg md:hidden">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="font-medium text-sm">{t.pwa?.bannerTitle ?? "Install EtheraGuru"}</p>
          <p className="text-xs opacity-90">
            {t.pwa?.bannerDescription ?? "Add to home screen for quick access"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handleInstall}>
            <Download className="w-4 h-4 mr-1" />
            {t.pwa?.install ?? "Install"}
          </Button>
          <Button size="icon" variant="ghost" onClick={handleDismiss} className="shrink-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
